import es from "./locales/es.json";
import en from "./locales/en.json";
import de from "./locales/de.json";
import ru from "./locales/ru.json";

const LANGUAGE_STORAGE_KEY = "xima_ui_language";
const supported = ["es", "en", "de", "ru"];
const resources = { es, en, de, ru };

const resolveLanguage = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (supported.includes(normalized)) return normalized;
  const base = normalized.split(/[-_]/)[0];
  return supported.includes(base) ? base : null;
};

const normalizeLanguage = (value) => resolveLanguage(value) || "es";

export function detectBrowserLanguage() {
  if (typeof window === "undefined") return "es";

  const stored = resolveLanguage(window.localStorage?.getItem(LANGUAGE_STORAGE_KEY));
  if (stored) return stored;

  const browserLanguages = [
    window.navigator?.language,
    ...(Array.isArray(window.navigator?.languages) ? window.navigator.languages : []),
  ];

  for (const candidate of browserLanguages) {
    const normalized = resolveLanguage(candidate);
    if (normalized) return normalized;
  }

  return "es";
}

export let currentLang = detectBrowserLanguage();

export function setLanguage(lang) {
  currentLang = normalizeLanguage(lang);
  if (typeof window !== "undefined") {
    window.localStorage?.setItem(LANGUAGE_STORAGE_KEY, currentLang);
    document.documentElement.lang = currentLang;
    window.dispatchEvent(
      new CustomEvent("xima:languagechange", { detail: { language: currentLang } })
    );
  }
  return currentLang;
}

export function getCurrentLanguage() {
  if (typeof window === "undefined") return currentLang || "es";
  const stored = window.localStorage?.getItem(LANGUAGE_STORAGE_KEY);
  currentLang = normalizeLanguage(stored || currentLang || detectBrowserLanguage());
  return currentLang;
}

const getByPath = (resource, key) => {
  if (!resource || !key) return undefined;
  const directValue = resource[key];
  if (typeof directValue === "string") return directValue;

  const parts = String(key).split(".");
  let cursor = resource;
  for (const part of parts) {
    if (!cursor || typeof cursor !== "object") return undefined;
    cursor = cursor[part];
  }
  return typeof cursor === "string" ? cursor : undefined;
};

const getResourceValue = (language, key) => {
  const normalized = normalizeLanguage(language);
  const resource = resources[normalized] || resources.es;
  return (
    getByPath(resource, key) ||
    getByPath(resource, `legacy.${key}`) ||
    getByPath(resources.es, key) ||
    getByPath(resources.es, `legacy.${key}`)
  );
};

const interpolate = (value, params = {}) => {
  if (!value || typeof value !== "string") return value;
  return value.replace(/\{(\w+)\}/g, (match, name) => {
    if (Object.prototype.hasOwnProperty.call(params, name)) {
      return String(params[name]);
    }
    return match;
  });
};

export function t(key, params) {
  const value = getResourceValue(getCurrentLanguage(), key);
  return interpolate(value || getResourceValue("es", key) || key, params);
}

const flattenResource = (resource, prefix = "", output = []) => {
  Object.entries(resource || {}).forEach(([key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") {
      output.push([nextKey, value]);
      return;
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenResource(value, nextKey, output);
    }
  });
  return output;
};

const sourceToKey = (() => {
  const map = new Map();
  for (const [key, value] of flattenResource(resources.es)) {
    map.set(String(value).trim(), key);
  }
  for (const [key, value] of flattenResource(resources.en)) {
    map.set(String(value).trim(), key);
  }
  return map;
})();

export function translateText(sourceText, options = {}) {
  const source = String(sourceText || "").trim();
  if (!source) return "";

  const language = normalizeLanguage(options.language || getCurrentLanguage());
  if (language === "es") return options.fallbackEs || source;
  if (language === "en" && options.fallbackEn) return options.fallbackEn;

  const key = sourceToKey.get(source);
  if (key) {
    return getResourceValue(language, key) || getResourceValue("es", key) || source;
  }

  if (language === "en") return options.fallbackEn || source;
  return source;
}
